import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react"

import { cn } from "@/domains/library"

export interface RevealProps {
  readonly children: ReactNode
  readonly className?: string
  /** Delay before the reveal transition starts, in milliseconds. Use to stagger sibling reveals. */
  readonly delayMs?: number
}

/**
 * Generic scroll-reveal wrapper. The first time its children enter the viewport
 * they fade and lift into place. Purely presentational and Storybook-safe: when
 * IntersectionObserver is unavailable the content renders immediately, and the
 * reduced-motion media query (globals.css) disables the transition entirely.
 */
export function Reveal({ children, className, delayMs = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (node == null) return

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.unobserve(entry.target)
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn("reveal", isVisible && "reveal--visible", className)}
      style={{ "--reveal-delay": `${delayMs}ms` } as CSSProperties}
    >
      {children}
    </div>
  )
}
